<template>
  <div class="page-root">
    <kitchen-drawer v-model="drawerOpen" :limited="isDonePageOnly" />

    <div class="header shrink-0">
      <template v-if="mobile">
        <div class="flex flex-1 items-center gap-2">
          <UButton
            v-if="route.query?.from"
            color="neutral"
            variant="ghost"
            icon="i-lucide-arrow-left"
            :ui="{ leadingIcon: 'size-5' }"
            :aria-label="t('general.back')"
            @click="router.push({ name: route.query.from as string })"
          />

          <connection-management-button />
        </div>

        <div class="flex flex-1 items-center justify-center">
          <!-- Active view = solid primary pill, the other outline (P15). -->
          <template v-if="!isDonePageOnly">
            <UButton
              class="status-tab-btn"
              :color="!isDonePage ? 'primary' : 'neutral'"
              :variant="!isDonePage ? 'solid' : 'outline'"
              size="sm"
              icon="i-lucide-folder-open"
              @click="router.push({ name: 'kitchenOpenTickets', query: route.query }).catch(err => { })"
            >
              {{ openTicketsCount }}
            </UButton>

            <UButton
              class="status-tab-btn"
              :color="isDonePage ? 'primary' : 'neutral'"
              :variant="isDonePage ? 'solid' : 'outline'"
              size="sm"
              icon="i-lucide-folder"
              @click="router.push({ name: 'kitchenDoneTickets', query: route.query }).catch(err => { })"
            >
              {{ doneTicketsCount }}
            </UButton>
          </template>

          <span v-else>{{ t('general.kitchen') }}</span>
        </div>

        <div class="flex flex-1 items-center justify-end gap-2">
          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-menu"
            :ui="{ leadingIcon: 'size-5' }"
            :aria-label="t('kds.drawer.settings')"
            @click="drawerOpen = !drawerOpen"
          />
        </div>
      </template>

      <template v-else>
        <div class="flex flex-1 items-center gap-2">
          <UButton
            v-if="route.query?.from"
            color="neutral"
            variant="ghost"
            icon="i-lucide-arrow-left"
            :ui="{ leadingIcon: 'size-5' }"
            :aria-label="t('general.back')"
            @click="router.push({ name: route.query.from as string })"
          />

          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-refresh-cw"
            :ui="{ leadingIcon: 'size-5' }"
            :aria-label="t('general.refresh')"
            @click.stop="refreshTickets"
          />

          <connection-management-button />
        </div>

        <!-- Active view = solid primary pill, the other outline (P15). -->
        <div class="flex flex-1 items-center justify-center">
          <UButton
            v-if="!isDonePageOnly"
            class="status-tab-btn"
            :color="!isDonePage ? 'primary' : 'neutral'"
            :variant="!isDonePage ? 'solid' : 'outline'"
            @click="router.push({ name: 'kitchenOpenTickets', query: route.query }).catch(err => { })"
          >
            <span class="mr-1">{{ openTicketsCount }}</span>
            <span>{{ $t('general.opened') }}</span>
          </UButton>

          <UButton
            class="status-tab-btn"
            :color="isDonePage ? 'primary' : 'neutral'"
            :variant="isDonePage ? 'solid' : 'outline'"
            @click="router.push({ name: 'kitchenDoneTickets', query: route.query }).catch(err => { })"
          >
            <span class="mr-1">{{ doneTicketsCount }}</span>
            <span>{{ $t('general.completed') }}</span>
          </UButton>
        </div>

        <div class="flex flex-1 items-center justify-end gap-3">
          <div
            v-if="!isKDSTerminal && store.user"
            class="flex items-center gap-1"
          >
            <!-- P15: quiet muted label beside the switch. -->
            <div
              class="cursor-pointer p-3 text-muted"
              @click="setKdsSettings(!filterForUser)"
            >
              {{ $t('general.mine_only') }}
            </div>

            <USwitch
              v-model="filterForUser"
              @update:model-value="setKdsSettings(undefined)"
            />
          </div>

          <div
            v-if="!isDonePage && totalPages > 0"
            class="paginate-btns"
          >
            <UButton
              v-for="(itemsTable, index) in itemsTables"
              :key="index"
              class="page-map-btn"
              color="neutral"
              :variant="index + 1 === page ? 'soft' : 'outline'"
              size="sm"
              @click="setPage(index + 1)"
            >
              <table>
                <tbody>
                  <tr v-for="itemsTableRow in itemsTable">
                    <td :class="getItemStatusClass(item)" v-for="item in itemsTableRow">
                    </td>
                  </tr>
                </tbody>
              </table>
            </UButton>
          </div>

          <div
            v-else-if="totalPages > 0"
            class="flex items-center gap-1"
          >
            <UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-chevron-left"
              :ui="{ leadingIcon: 'size-5' }"
              :aria-label="t('general.back')"
              :disabled="page <= 1"
              @click="setPage('--')"
            />

            <span class="text-identifier min-w-6 text-center">{{ page }}</span>

            <UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-chevron-right"
              :ui="{ leadingIcon: 'size-5' }"
              :aria-label="t('general.next')"
              :disabled="totalPages === page || totalPages === 0"
              @click="setPage('++')"
            />
          </div>

          <UButton
            v-if="!isDonePage"
            color="neutral"
            variant="ghost"
            icon="i-lucide-menu"
            :ui="{ leadingIcon: 'size-5' }"
            :aria-label="t('kds.drawer.settings')"
            @click="drawerOpen = !drawerOpen"
          />
        </div>
      </template>
    </div>

    <!-- The ticket grid owns the P15 frame padding (6/18/18). -->
    <main class="relative min-h-0 flex-1 overflow-hidden">
      <router-view />
    </main>

    <div
      v-if="mobile"
      class="footer shrink-0"
    >
      <div class="flex flex-1 items-center">
        <div
          v-if="!isKDSTerminal && store.user"
          class="mx-3"
          :class="filterForUser ? 'text-success' : 'text-muted'"
        >
          <UIcon name="i-lucide-user" class="size-5" />
        </div>

        <USwitch
          v-if="!isKDSTerminal && store.user"
          v-model="filterForUser"
          color="success"
          @update:model-value="setKdsSettings(undefined)"
        />
      </div>

      <div class="flex flex-1 items-center justify-center">
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-chevron-left"
          :ui="{ leadingIcon: 'size-5' }"
          :aria-label="t('general.back')"
          :disabled="page <= 1"
          @click="setPage('--')"
        />

        <span class="text-identifier min-w-6 text-center">{{ page }}</span>

        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-chevron-right"
          :ui="{ leadingIcon: 'size-5' }"
          :aria-label="t('general.next')"
          :disabled="totalPages === page || totalPages === 0"
          @click="setPage('++')"
        />
      </div>

      <div class="flex flex-1 items-center justify-end">
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :ui="{ leadingIcon: 'size-5' }"
          :aria-label="t('general.refresh')"
          @click.stop="refreshTickets"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, onBeforeMount, Ref } from 'vue';
import { UserService, Store, appStorage, KitchenTicket, IKDSSettings, AppStorageKey } from 'app-core';
import { useRouter, useRoute } from 'vue-router';
import { useAppStore } from '@/stores/useAppStore';
import { useDisplay } from '@/composables/useDisplay';
import { useI18n } from 'vue-i18n';
import ConnectionManagementButton from '@/components/ConnectionManagementButton.vue';
import KitchenDrawer from './KitchenDrawer.vue';
import useKitchenTickets from '@/composables/useKitchenTickets';

interface ExtendedKitchenTicket {
  warningColor: string
  page: number
  id: string
  ticket: KitchenTicket
}

const store = useAppStore()
const route = useRoute()
const router = useRouter()

const { mobile } = useDisplay()
const { t } = useI18n()

const {
  openTickets,
  doneTickets,
  openTicketsCount,
  doneTicketsCount,
  gridRows,
  gridCols,
  openPage,
  donePage,
  totalOpenPages,
  totalDonePages,
  fetchLatest,
  clearFetchLatestInterval,
  refreshTickets,
} = useKitchenTickets();

const isDonePageOnly = ref(false);
const isKDSTerminal: Ref<boolean | undefined> = ref(false);
const filterForUser = ref(false);
const drawerOpen = ref(false);

const isDonePage = computed(() => route.name === 'kitchenDoneTickets');

const page = computed(() => (isDonePage.value ? donePage.value : openPage.value));

const totalPages = computed(() => (isDonePage.value ? totalDonePages.value : totalOpenPages.value));

const setPage = (newPage: string | number) => {
  if (newPage === '--') {
    if (isDonePage.value) {
      donePage.value = Math.max(1, donePage.value - 1);
      openPage.value = 1;
    } else {
      openPage.value = Math.max(1, openPage.value - 1);
      donePage.value = 1;
    }
  } else if (newPage === '++') {
    if (isDonePage.value) {
      donePage.value += 1;
      openPage.value = 1;
    } else {
      openPage.value += 1;
      donePage.value = 1;
    }
  } else {
    const parsedPage = typeof newPage === 'number' ? newPage : Number(newPage);

    if (!isNaN(parsedPage)) {
      if (isDonePage.value) {
        donePage.value = parsedPage;
        openPage.value = 1;
      } else {
        openPage.value = parsedPage;
        donePage.value = 1;
      }
    }
  }
};

const getItems = (page: number) => {
  const items = route.name === 'kitchenDoneTickets' ? doneTickets.value : openTickets.value;
  return items.filter((order) => order.page === page);
};

function getItemsTable(page: number) {
  const items = getItems(page)
  const itemsTable: ExtendedKitchenTicket[][] = []

  for(let row = 0; row < gridRows.value; row++) {
    itemsTable.push([])
    for(let col = 0; col < gridCols.value; col++) {
      itemsTable[row].push(items[row * gridCols.value + col])
    }
  }

  return itemsTable
}

const itemsTables = computed(() => {
  const itemsTables = []
  for(let page = 1; page <= totalPages.value; page++) {
    itemsTables.push(getItemsTable(page))
  }

  return itemsTables
})

const setKdsSettings = (_value: boolean | undefined) => {
  if (_value !== undefined) filterForUser.value = _value;

  const kdsSettings = {
    ...store.kdsSettings, ...{ filterForUser: filterForUser.value }
  };

  appStorage.local().setItem(AppStorageKey.kdsSettings, kdsSettings);

  Store.set('kdsSettings', kdsSettings);
};

const getItemStatusClass = (item?: ExtendedKitchenTicket) => {
  if(!item) return undefined

  let itemClass = 'neutral-bg';

  if (item.warningColor === 'yellow') {
    itemClass = 'warning-bg';
  }

  if (item.warningColor === 'red') {
    itemClass = 'error-bg';
  }

  return itemClass;
};

onBeforeMount(async () => {
  const storedSettings = await appStorage.local().getItem<IKDSSettings>(AppStorageKey.kdsSettings);
  if (storedSettings) Store.set('kdsSettings', storedSettings);
});

onMounted(async () => {
  filterForUser.value = store.kdsSettings?.filterForUser || false;
  isKDSTerminal.value = store.terminal?.isKitchenDisplaySystem();
  isDonePageOnly.value = !isKDSTerminal.value && !UserService.allows('access_terminals');

  fetchLatest();
});

onUnmounted(() => {
  clearFetchLatestInterval();
});
</script>

<style lang="scss" scoped>
// P15 topbar: 56px row on the paper canvas, 18px side padding.
.header {
  display: flex;
  align-items: center;
  min-height: 56px;
  // A raw-div header must add the status-bar inset itself where the page
  // draws under the bar (a browser). The native apps keep the web view clear
  // of the bars and set var(--app-safe-area-top) to 0 (useSystemBars).
  padding: var(--app-safe-area-top, 0px) 18px 0;

  .status-tab-btn {
    // Uppercase + tracking come from the theme's button base (P15 reads
    // "10 OTVORENO" / "0 ZAVRŠENO" out of the box).
    min-width: 150px;
    margin: 0 4px;

    @media screen and (max-width: 600px) {
      padding: 0 8px;
      min-width: 66px;
    }
  }

  // P15 page map: one compact glyph per page — bordered rounded frame with
  // sand/amber/red cells mirroring that page's ticket urgency.
  .paginate-btns {
    display: flex;
    justify-content: flex-end;
    gap: 4px;

    .page-map-btn {
      margin: 0;
      width: 38px;
      height: 30px;
      padding: 3px;
    }

    table {
      width: 100%;
      height: 100%;
      table-layout: fixed;
      border-spacing: 1.5px;
      border-collapse: separate;

      td {
        border-radius: 1px;

        &.error-bg {
          background-color: var(--ui-error);
        }

        &.warning-bg {
          background-color: var(--ui-warning);
        }

        &.neutral-bg {
          background-color: var(--ui-bg-accented);
        }
      }
    }
  }
}

.footer {
  display: flex;
  align-items: center;
  min-height: 52px;
  padding: 4px 12px calc(4px + var(--app-safe-area-bottom, env(safe-area-inset-bottom)));
}
</style>
